import { createApp } from 'vue'
import { mapState, mapGetters, mapActions } from 'vuex'

/**
 * Import vue reactivity & watchers mechanics and vuex mapping in regular
 * objects. The point is not only to simplify the code in services and limit
 * the risk of error/bugs in particular with regards to watchers and value
 * updates.
 *
 * @since KJS-4782
 */
export default class ReactiveBridge {
  constructor(target) {
    this.target = target
    this.opts = {
      store: null,
      data: {},
      computed: {},
      watchers: {},
      methods: {}
    }
    this.vm = null
  }

  build() {
    const bridge = this

    const app = createApp({
      data() {
        return bridge.opts.data
      },
      computed: this.opts.computed,
      watch: this._bindWatchersContext(),
      methods: this.opts.methods,
      render: () => null
    })

    if (this.opts.store) {
      app.use(this.opts.store)
    }

    this.vm = app.mount(document.createElement('div'))
    this._bind()
  }

  /**
   * @param {VuexStore} $store
   * @returns {this}
   */
  store($store) {
    this.opts.store = $store
    return this
  }

  /**
   * @param {Array} properties
   * @returns {this}
   */
  declare(properties) {
    for (const name of properties) {
      this.opts.data[name] = this.target[name]
    }
    return this
  }

  /**
   * @param {Object} properties
   * @returns {this}
   */
  data(properties) {
    for (const name in properties) {
      this.opts.data[name] = properties[name]
    }
    return this
  }

  /**
   * @param {{ [key: string]: Function }} properties
   * @returns {this}
   */
  computed(properties) {
    for (const name in properties) {
      this.opts.computed[name] = properties[name]
    }
    return this
  }

  /**
   * @param {Object} properties
   * @returns {this}
   */
  state(properties) {
    this.computed(mapState(properties))
    return this
  }

  /**
   * @param {Object} properties
   * @returns {this}
   */
  getters(properties) {
    this.computed(mapGetters(properties))
    return this
  }

  /**
   * @param {Object} properties
   * @returns {this}
   */
  actions(actionNames) {
    const methods = mapActions(actionNames)

    for (const name in methods) {
      this.opts.methods[name] = methods[name]
    }
    return this
  }

  /**
   * @param {{ [key: string]: Function }} properties
   * @returns {this}
   */
  watch(callbacks) {
    this.opts.watchers = { ...this.opts.watchers, ...callbacks }
    return this
  }

  /**
   * Bind watcher functions to the original object's context (target)
   *
   * @returns {{ [key: string]: Function }}
   */
  _bindWatchersContext() {
    const w = {}
    for (const name in this.opts.watchers) {
      w[name] = this.opts.watchers[name].bind(this.target)
    }
    return w
  }

  /**
   * For any property (data, computed, methods) created in internal vm, create
   * a property in target object with and getter/setter creating a bridge
   * with vm value.
   */
  _bind() {
    for (const name in this.opts.data) {
      this._bindTwoWay(name)
    }
    for (const name in this.opts.computed) {
      const userDef = this.opts.computed[name]
      if (typeof userDef === 'function' || typeof userDef.set !== 'function') {
        this._bindOneWay(name)
      } else {
        this._bindTwoWay(name)
      }
    }
    for (const name in this.opts.methods) {
      this._bindOneWay(name)
    }
  }

  _bindTwoWay(name) {
    const vm = this.vm

    Object.defineProperty(this.target, name, {
      configurable: false,
      enumerable: true,
      get() {
        return vm[name]
      },
      set(value) {
        vm[name] = value
      }
    })
  }

  _bindOneWay(name) {
    const vm = this.vm

    Object.defineProperty(this.target, name, {
      configurable: false,
      enumerable: true,
      get() {
        return vm[name]
      },
      set() {
        throw new Error(`Cannot mutate read-only property ${name}`)
      }
    })
  }
}
